import { inject, Injectable } from '@angular/core';
import { CookieHelperService, LoggingService } from '@com-delta-idp/mnx-utils';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, concatMap, shareReplay } from 'rxjs/operators';
import {
  AuthAccessTokenDetails,
  LoginOAuthParams,
  PkceCode,
  RedirectfulResponse
} from '../../../models';
import { DashboardResponse } from '../../../session';
import { LoginRememberMeUtilsService } from '../login-remember-me-utils-service/login-remember-me-utils.service';
import { LoginRememberMeWrapperService } from '../login-remember-me-wrapper-service/login-remember-me-wrapper.service';
import { UserDataService } from './user-data.service';

@Injectable({
  providedIn: 'root'
})
export class UserSessionHelperExtendedService {
  protected userData = inject(UserDataService);
  protected loginRememberMeWrapper = inject(LoginRememberMeWrapperService);
  protected logging = inject(LoggingService);
  protected loginRememberMeUtils = inject(LoginRememberMeUtilsService);
  protected cookieHelper = inject(CookieHelperService);
  protected data$!: Observable<DashboardResponse>;
  protected sessionRequests = new Map<string, Observable<DashboardResponse>>();

  pkceCode = {} as PkceCode;
  tokenAvailable = false;
  tokenExpiry = 0;
  tokenIssuedTime = 0;
  tokenValue$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  removeSessionValues(): void {
    if (
      window.localStorage.getItem('uid') ||
      window.localStorage.getItem('encyToken') ||
      this.cookieHelper.getCookieValue('accountType')
    ) {
      window.localStorage.removeItem('uid');
      window.localStorage.removeItem('encyToken');
      this.cookieHelper.setCookieName('accountType', '', 0, false);
    }
  }

  async guestStatus(): Promise<void> {
    if (this.loginRememberMeUtils.checkServiceWorkerController()) {
      await this.loginRememberMeUtils.sendMessage({
        type: 'ADD_STATUS',
        eamStatus: 'GUEST'
      });
    }
  }

  nonLoggedInStateHelper(error?: string): Observable<DashboardResponse> {
    this.guestStatus();
    this.removeSessionValues();
    this.tokenValue$.next(true);
    if (error) {
      this.logging.log(error);
    }
    return of({} as DashboardResponse);
  }

  getTokenAndUserSessionData(
    loginOAuthParams: LoginOAuthParams
  ): Observable<DashboardResponse> {
    return this.loginRememberMeWrapper
      .initiatePingFlow(loginOAuthParams, 'INITIATE_LOGIN')
      .pipe(
        concatMap(
          (
            response:
              | AuthAccessTokenDetails
              | RedirectfulResponse
              | boolean
              | string
          ) => {
            if (typeof response === 'string' || typeof response === 'boolean') {
              return this.nonLoggedInStateHelper();
            } else if ('access_token' in response) {
              this.loginRememberMeUtils.sendTokenServiceWorker(
                loginOAuthParams.oauthConfig,
                response
              );
              this.tokenValue$.next(true);
              this.data$ = this.userData
                .getDashBrdData(
                  loginOAuthParams.oauthConfig,
                  response.access_token
                )
                .pipe(shareReplay(1));
              return this.data$;
            } else {
              return this.nonLoggedInStateHelper();
            }
          }
        ),
        catchError((error) => {
          return this.nonLoggedInStateHelper(error);
        })
      );
  }
}
